import { Outlet, useMatch, useLocation } from 'react-router-dom'
import { useIsDesktop } from '@/hooks/use-media-query'
import ContactsPage from '@/pages/ContactsPage'
import { RouteErrorBoundary } from '@/components/RouteErrorBoundary'

/**
 * Layout wrapper for /contacts routes.
 *
 * On md+ screens: renders a 2-panel split view with the contacts list
 * permanently visible on the left and the active child route (detail, edit, etc.)
 * on the right via <Outlet />.
 *
 * On mobile (<md): renders only the matched child route (single-column).
 */
export function ContactsLayout() {
  const isDesktop = useIsDesktop()
  const location = useLocation()

  // Extract contact id from child routes like /contacts/:id, /contacts/:id/edit, etc.
  const detailMatch = useMatch('/contacts/:id/*')
  const rawId = detailMatch?.params.id
  // Exclude non-UUID segments like "new" from being treated as a contact id
  const contactId = rawId && rawId !== 'new' ? rawId : undefined

  // On mobile, just render the child route as-is (single column)
  if (!isDesktop) {
    return <Outlet />
  }

  // On desktop, show split panel
  const isListOnly = location.pathname === '/contacts' || location.pathname === '/contacts/'

  return (
    <div className="flex h-full">
      {/* Left panel — contact list */}
      <div
        className={`border-border isolate shrink-0 overflow-y-auto border-r ${
          isListOnly ? 'w-full' : 'w-[380px]'
        }`}
      >
        <RouteErrorBoundary>
          <ContactsPage selectedContactId={contactId} embedded />
        </RouteErrorBoundary>
      </div>

      {/* Right panel — detail / edit / interaction */}
      {!isListOnly && (
        <div className="flex-1 overflow-y-auto pb-20">
          <Outlet />
        </div>
      )}
    </div>
  )
}
